import React, { useState, useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Drawer,
  Checkbox,
  FormControlLabel,
  Divider,
  Popover,
  Button,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SpeedIcon from '@mui/icons-material/Speed';
import StorageIcon from '@mui/icons-material/Storage';
import { useApi } from '../contexts/ApiContext';
import { useAuth, useSelectedWorkspace } from '../contexts/AuthContext';
import { ChartData, MetricsSummary } from '../types/stats';
import { useWithTokenRefresh } from '../utils/api';
import {
  buildTimeSeriesParams,
  buildBreakdownParams,
  formatV2Series,
  type DatePreset,
} from '../utils/v2StatsHelpers';
import searchIcon from '../assets/reports/search.svg';
// Use the same provider icons as StorageUnits.tsx for visual consistency
import iconStorj from '../assets/icons-new-bucket/storj.svg';
import iconAWS from '../assets/icons-new-bucket/aws.svg';
import iconGoogle from '../assets/cloud-providers/google-cloud-icon.svg';
import iconAzure from '../assets/icons-new-bucket/azure.svg';
import iconFilecoin from '../assets/icons-new-bucket/filecoin.svg';
import iconOther from '../assets/icons-new-bucket/other.svg';

/**
 * Arrondit une valeur max vers le haut pour créer un domaine d'axe Y propre
 * GARANTIT un résultat divisible par 5 pour afficher 5 paliers égaux
 * S'adapte automatiquement à toutes les plages de valeurs
 */
const roundToNiceDomain = (maxValue: number): number => {
  if (maxValue === 0) return 0.5;
  if (maxValue < 0.001) return 0.005;
  
  const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
  
  const possibleSteps = [
    magnitude * 0.5,
    magnitude * 1,
    magnitude * 1.5,
    magnitude * 2,
    magnitude * 2.5,
    magnitude * 5,
    magnitude * 10,
  ];
  
  for (const step of possibleSteps) {
    if (step >= maxValue) {
      return step;
    }
  }
  
  return magnitude * 10;
};

// Simple calendar month view for the date popover
function MonthView({
  offset = 0,
  startDate,
  endDate,
  onSelect,
  baseDate,
  onPrevMonth,
  onNextMonth,
}: {
  offset?: number;
  startDate: Date | null;
  endDate: Date | null;
  onSelect: (d: Date) => void;
  baseDate?: Date;
  onPrevMonth?: () => void;
  onNextMonth?: () => void;
}) {
  const base = baseDate ? new Date(baseDate) : new Date();
  const year = base.getFullYear();
  const month = base.getMonth() + offset; // can overflow, Date will normalize
  const first = new Date(year, month, 1);
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const displayMonth = first.getMonth();
  const displayYear = first.getFullYear();

  // Build cells: leading blanks then days 1..N
  const leading = (firstDay + 6) % 7; // shift to Mon=0 for Mo-Su display
  const cells: (number | null)[] = [
    ...Array(leading).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  // pad to full weeks
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: 6,
          pb: 6,
          mb: 1.5,
        }}
      >
        {/* Left arrow (only if handler provided) */}
        {onPrevMonth && (
          <IconButton
            size="small"
            onClick={onPrevMonth}
            sx={{ position: 'absolute', left: 8, color: '#98A2B3' }}
            aria-label="Previous month"
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
        <Typography sx={{ color: '#101828', fontWeight: 600 }}>
          {monthNames[displayMonth]} {displayYear}
        </Typography>
        {/* Right arrow (only if handler provided) */}
        {onNextMonth && (
          <IconButton
            size="small"
            onClick={onNextMonth}
            sx={{ position: 'absolute', right: 8, color: '#98A2B3' }}
            aria-label="Next month"
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={{ px: 1.5, pb: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            mb: 1,
            color: '#667085',
            fontSize: 12,
          }}
        >
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (
            <Box key={d} sx={{ textAlign: 'center' }}>
              {d}
            </Box>
          ))}
        </Box>
        <Box
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', columnGap: 0, rowGap: 0.5 }}
        >
          {cells.map((day, idx) => {
            const d = day ? new Date(displayYear, displayMonth, day) : null;
            const isStart = !!d && !!startDate && d.toDateString() === startDate.toDateString();
            const isEnd = !!d && !!endDate && d.toDateString() === endDate.toDateString();
            const inRange =
              !!d &&
              !!startDate &&
              !!endDate &&
              d.getTime() > Math.min(startDate.getTime(), endDate.getTime()) &&
              d.getTime() < Math.max(startDate.getTime(), endDate.getTime());
            const showBubble = !!day && (isStart || isEnd);
            // Determine rounded corners for gray tunnel at row/month edges
            const col = idx % 7;
            const prevCell = col === 0 ? null : cells[idx - 1];
            const nextCell = col === 6 ? null : cells[idx + 1];
            const prevDate = prevCell
              ? new Date(displayYear, displayMonth, prevCell as number)
              : null;
            const nextDate = nextCell
              ? new Date(displayYear, displayMonth, nextCell as number)
              : null;
            const prevInRange =
              !!prevDate &&
              !!startDate &&
              !!endDate &&
              prevDate.getTime() > Math.min(startDate.getTime(), endDate.getTime()) &&
              prevDate.getTime() < Math.max(startDate.getTime(), endDate.getTime());
            const nextInRange =
              !!nextDate &&
              !!startDate &&
              !!endDate &&
              nextDate.getTime() > Math.min(startDate.getTime(), endDate.getTime()) &&
              nextDate.getTime() < Math.max(startDate.getTime(), endDate.getTime());
            const leftRounded = inRange && (!prevInRange || col === 0);
            const rightRounded = inRange && (!nextInRange || col === 6);
            return (
              <Box
                key={idx}
                onClick={() => day && onSelect(new Date(displayYear, displayMonth, day))}
                sx={{
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: day ? 'pointer' : 'default',
                }}
              >
                <Box
                  sx={{
                    width: showBubble ? 40 : inRange ? '100%' : 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: showBubble
                      ? '50%'
                      : inRange
                      ? leftRounded && rightRounded
                        ? '20px'
                        : leftRounded
                        ? '20px 0 0 20px'
                        : rightRounded
                        ? '0 20px 20px 0'
                        : 0
                      : '8px',
                    backgroundColor: showBubble ? '#617BFF' : inRange ? '#F2F4F7' : 'transparent',
                    color: day ? (showBubble ? '#FFFFFF' : '#101828') : 'transparent',
                    '&:hover': day
                      ? { backgroundColor: showBubble ? '#617BFF' : '#F2F4F7' }
                      : undefined,
                  }}
                >
                  {day ?? ''}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

const MetricsDashboard: React.FC = () => {
  const { apiClient } = useApi();
  const { globalData } = useAuth();
  const withTokenRefresh = useWithTokenRefresh();


  // États pour les données avec gestion améliorée du chargement
  const [loading, setLoading] = useState<boolean>(true);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [metricsSummary, setMetricsSummary] = useState<MetricsSummary>({
    averageUploaded: 0,
    averageDownloaded: 0,
    averageSizeChange: 0,
    totalRequests: 0,
  });

  // États pour les données formatées pour les graphiques
  const [uploadChartData, setUploadChartData] = useState<ChartData>({ series: [], xAxisData: [] });
  const [downloadChartData, setDownloadChartData] = useState<ChartData>({
    series: [],
    xAxisData: [],
  });
  const [storageChartData, setStorageChartData] = useState<ChartData>({
    series: [],
    xAxisData: [],
  });
  const [requestsChartData, setRequestsChartData] = useState<ChartData>({
    series: [],
    xAxisData: [],
  });
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [providerExpanded, setProviderExpanded] = useState<boolean>(false);
  const [selectedProviders, setSelectedProviders] = useState<Set<string>>(new Set());
  const [selectedRepositories, setSelectedRepositories] = useState<Set<string>>(new Set());
  const [selectedBuckets, setSelectedBuckets] = useState<Set<string>>(new Set());
  const [repoExpanded, setRepoExpanded] = useState<boolean>(false);
  const [bucketExpanded, setBucketExpanded] = useState<boolean>(false);
  // Date picker state
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false);
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string>('Last 30 days');
  // Initialiser les dates avec les valeurs par défaut pour 'Last 30 days' (jours complets, jusqu'à hier)
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(() => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(23, 59, 59, 999);
    const start = new Date(yesterday.getTime() - 29 * 24 * 60 * 60 * 1000);
    start.setHours(0, 0, 0, 0);
    return start;
  });
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(() => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(23, 59, 59, 999);
    return yesterday;
  });
  const [calendarBaseDate, setCalendarBaseDate] = useState<Date>(new Date());

  // Helpers to compute start/end of common periods (local timezone)
  const startOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const endOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  };
  const startOfWeekMonday = (d: Date) => {
    // Make Monday the first day of week (Mo=1..Su=7)
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    const day = x.getDay(); // 0=Sun .. 6=Sat
    const diffToMonday = (day + 6) % 7; // 0 if Monday, 6 if Sunday
    x.setDate(x.getDate() - diffToMonday);
    return x;
  };
  const endOfWeekMonday = (d: Date) => {
    const start = startOfWeekMonday(d);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return end;
  };
  const startOfMonth = (d: Date) => {
    const x = new Date(d.getFullYear(), d.getMonth(), 1);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const endOfMonth = (d: Date) => {
    const x = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    x.setHours(23, 59, 59, 999);
    return x;
  };
  const startOfYear = (d: Date) => {
    const x = new Date(d.getFullYear(), 0, 1);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const endOfYear = (d: Date) => {
    const x = new Date(d.getFullYear(), 11, 31);
    x.setHours(23, 59, 59, 999);
    return x;
  };

  const getRangeForPreset = (preset: string): { start: Date | null; end: Date | null } => {
    const now = new Date();
    switch (preset) {
      case 'Last 30 days': {
        // Utiliser des jours complets: de J-29 à J-1 (hier)
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const start = new Date(yesterday.getTime() - 29 * 24 * 60 * 60 * 1000);
        return { start: startOfDay(start), end: endOfDay(yesterday) };
      }
      case 'Today': {
        return { start: startOfDay(now), end: endOfDay(now) };
      }
      case 'Yesterday': {
        const y = new Date(now);
        y.setDate(y.getDate() - 1);
        return { start: startOfDay(y), end: endOfDay(y) };
      }
      case 'This week': {
        return { start: startOfWeekMonday(now), end: endOfWeekMonday(now) };
      }
      case 'Last week': {
        const lastWeekRef = new Date(startOfWeekMonday(now));
        lastWeekRef.setDate(lastWeekRef.getDate() - 7);
        return { start: startOfWeekMonday(lastWeekRef), end: endOfWeekMonday(lastWeekRef) };
      }
      case 'This month': {
        return { start: startOfMonth(now), end: endOfMonth(now) };
      }
      case 'Last month': {
        const lastMonthRef = new Date(now.getFullYear(), now.getMonth() - 1, 15);
        return { start: startOfMonth(lastMonthRef), end: endOfMonth(lastMonthRef) };
      }
      case 'This year': {
        return { start: startOfYear(now), end: endOfYear(now) };
      }
      case 'Last year': {
        const lastYearRef = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        return { start: startOfYear(lastYearRef), end: endOfYear(lastYearRef) };
      }
      case 'All time': {
        // Use nulls to represent no lower/upper bound, or choose a very early start
        return { start: null, end: endOfDay(now) };
      }
      default: {
        return { start: selectedStartDate, end: selectedEndDate };
      }
    }
  };

  // Update selected dates whenever a preset is chosen
  useEffect(() => {
    const { start, end } = getRangeForPreset(selectedPreset);
    setSelectedStartDate(start);
    setSelectedEndDate(end);
  }, [selectedPreset]);

  const formatDate = (d: Date) => {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${month}/${day}/${d.getFullYear()}`;
  };

  // Label to reflect the currently selected filter range
  const getSelectedRangeLabel = (): string => {
    // If a preset other than custom is selected, show it directly
    if (selectedPreset && selectedPreset !== 'Custom range') {
      return selectedPreset;
    }
    // Otherwise, show explicit dates
    if (selectedStartDate && selectedEndDate) {
      const sameDay = selectedStartDate.toDateString() === selectedEndDate.toDateString();
      if (sameDay) return formatDate(selectedStartDate);
      return `${formatDate(selectedStartDate)} - ${formatDate(selectedEndDate)}`;
    }
    return '';
  };

  const openDatePicker = (e: React.MouseEvent<HTMLElement>) => {
    setDateAnchorEl(e.currentTarget);
    setDatePickerOpen(true);
  };
  const closeDatePicker = () => setDatePickerOpen(false);

  const providerOptions: string[] = [
    'Google Cloud',
    'AWS',
    'Azure',
    'Alibaba Cloud',
    'FileCoin',
    'Backblaze B2',
    'Wasabi',
    'DigitalOcean Spaces',
    'OVH',
    'S3 Compatible',
  ];
  const providerIcons: Record<string, string> = {
    'Google Cloud': iconGoogle,
    AWS: iconAWS,
    Azure: iconAzure,
    FileCoin: iconFilecoin,
    Filecoin: iconFilecoin,
    Storj: iconStorj,
    Other: iconOther,
  };
  // Only keep providers that have a corresponding logo
  const providerList = providerOptions.filter((name) => !!providerIcons[name]);
  const [repoOptions, setRepoOptions] = useState<string[]>([]);
  const [bucketOptions, setBucketOptions] = useState<string[]>([]);

  // Use centralized workspace hook
  const globalSelectedWorkspaceId = useSelectedWorkspace();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>(globalSelectedWorkspaceId);

  // États de chargement granulaires
  const [listsLoaded, setListsLoaded] = useState<boolean>(false);

  // Filtered data based on selections
  const [filteredRepos, setFilteredRepos] = useState<any[]>([]);
  const [filteredBuckets, setFilteredBuckets] = useState<any[]>([]);
  // Track previous workspace to detect changes
  const prevWorkspaceIdRef = useRef<string>('ALL');
  // Flag to auto-apply selections once filtered lists are ready after a workspace change
  const pendingWorkspaceAutoSelectRef = useRef<boolean>(false);

  // Sync with global workspace selection from hook
  useEffect(() => {
    if (globalSelectedWorkspaceId && globalSelectedWorkspaceId !== selectedWorkspaceId) {
      setSelectedWorkspaceId(globalSelectedWorkspaceId);
      
      // Clear all filter selections when workspace changes
      setSelectedProviders(new Set());
      setSelectedRepositories(new Set());
      setSelectedBuckets(new Set());
      
      // Reset lists loaded flag to ensure proper sequencing
      setListsLoaded(false);
      // Mark that we should auto-select once lists are ready
      pendingWorkspaceAutoSelectRef.current = true;
    }
  }, [globalSelectedWorkspaceId]);

  // Load real repositories and buckets like in StorageRepos.tsx / StorageUnits.tsx
  useEffect(() => {
    let isMounted = true;

    // Ajouter un délai pour s'assurer que l'authentification est bien établie
    const loadLists = async () => {
      // Attendre un court instant pour s'assurer que le token est bien chargé
      await new Promise((resolve) => setTimeout(resolve, 500));

      if (!isMounted) return;

      try {
        // Charger les repos et les buckets séparément pour mieux gérer les erreurs
        try {
          // CRITICAL: Pass workspaceId to API call instead of filtering client-side
          const workspaceIdParam = selectedWorkspaceId && selectedWorkspaceId !== 'ALL' ? selectedWorkspaceId : undefined;
          const reposResponse = await withTokenRefresh(() => apiClient.getStorageRepos(workspaceIdParam));
          if (isMounted && reposResponse?.repos) {
            const repos = reposResponse.repos || [];
            // No need to filter - API already filtered by workspaceId
            const filteredReposList = repos;
            setFilteredRepos(filteredReposList);
            setRepoOptions(filteredReposList.map((r: any) => r.name).filter(Boolean));
          }
        } catch (repoError) {
          if (isMounted) {
            setRepoOptions([]);
            setFilteredRepos([]);
          }
        }

        try {
          // CRITICAL: Pass workspaceId to API call instead of filtering client-side
          const workspaceIdParam = selectedWorkspaceId && selectedWorkspaceId !== 'ALL' ? selectedWorkspaceId : undefined;
          const unitsResponse = await withTokenRefresh(() => apiClient.getStorageBuckets(workspaceIdParam));
          if (isMounted && unitsResponse?.buckets) {
            const units = unitsResponse.buckets || [];
            // No need to filter - API already filtered by workspaceId
            const filteredBucketsList = units;

            // Do not filter buckets by selected providers for the same reason as repos above.

            setFilteredBuckets(filteredBucketsList);
            setBucketOptions(filteredBucketsList.map((u: any) => u.name).filter(Boolean));
          }
        } catch (unitsError) {
          if (isMounted) {
            setBucketOptions([]);
            setFilteredBuckets([]);
          }
        }
      } catch (e) {
        if (!isMounted) return;
        setRepoOptions([]);
        setBucketOptions([]);
      }
    };

    loadLists();

    return () => {
      isMounted = false;
    };
  }, [apiClient, withTokenRefresh, selectedWorkspaceId, selectedProviders]);

  // Marquer les listes comme chargées après un délai
  useEffect(() => {
    const timer = setTimeout(() => {
      setListsLoaded(true);
    }, 800); // Délai pour s'assurer que les listes sont bien chargées
    return () => clearTimeout(timer);
  }, [repoOptions, bucketOptions]);

  // Preselect repos and buckets from the selected workspace in the Filters panel (do NOT auto-select providers)
  useEffect(() => {
    if (!listsLoaded) return;
    const curr = selectedWorkspaceId || 'ALL';

    if (curr === 'ALL') {
      // Clear explicit selections so metrics aggregate all
      setSelectedRepositories(new Set());
      setSelectedBuckets(new Set());
      return;
    }

    // Preselect all repos and buckets from the filtered lists (already workspace-scoped)
    const repoNames = filteredRepos.map((r: any) => r.name).filter(Boolean);
    const bucketNames = filteredBuckets.map((b: any) => b.name).filter(Boolean);

    setSelectedRepositories(new Set(repoNames));
    setSelectedBuckets(new Set(bucketNames));
  }, [selectedWorkspaceId, filteredRepos, filteredBuckets, listsLoaded]);

  // (moved below getWorkspaceProviders)

  const toggleProvider = (name: string) => {
    const wasSelected = selectedProviders.has(name);

    // Find repos that match this provider
    const relatedRepos = filteredRepos.filter((repo: any) => {
      const repoProviderName = getProviderNameFromStorageType(repo.storageType);
      const matches = repoProviderName === name;
      return matches;
    });

    // Find all buckets related to these repos
    const allRelatedBuckets: any[] = [];
    relatedRepos.forEach((repo: any) => {
      if (repo.buckets && repo.buckets.length > 0) {
        const repoBucketIds = repo.buckets
          .filter((bucket: any) => bucket.bucketId || bucket.unitId || bucket.id)
          .map((bucket: any) => bucket.bucketId || bucket.unitId || bucket.id);

        const relatedBuckets = filteredBuckets.filter((bucket: any) => {
          return repoBucketIds.includes(bucket.id);
        });

        allRelatedBuckets.push(...relatedBuckets);
      }
    });

    if (wasSelected) {
      // Deselecting provider
      setSelectedProviders((prev) => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });

      setSelectedRepositories((prev) => {
        const next = new Set(prev);
        relatedRepos.forEach((repo) => next.delete(repo.name));
        return next;
      });

      setSelectedBuckets((prev) => {
        const next = new Set(prev);
        allRelatedBuckets.forEach((bucket) => next.delete(bucket.name));
        return next;
      });
    } else {
      // Selecting provider
      setSelectedProviders((prev) => {
        const next = new Set(prev);
        next.add(name);
        return next;
      });

      setSelectedRepositories((prev) => {
        const next = new Set(prev);
        relatedRepos.forEach((repo) => next.add(repo.name));
        return next;
      });

      setSelectedBuckets((prev) => {
        const next = new Set(prev);
        allRelatedBuckets.forEach((bucket) => next.add(bucket.name));
        return next;
      });
    }
  };

  const toggleRepository = (name: string) => {
    const selectedRepo = filteredRepos.find((r: any) => r.name === name);

    setSelectedRepositories((prev) => {
      const next = new Set(prev);
      const wasSelected = next.has(name);

      if (wasSelected) {
        next.delete(name);

        // Auto-deselect related buckets when deselecting a repository
        if (selectedRepo) {
          // Use the same approach as StorageRepoDetails: repo.buckets contains bucketId references
          const repoBucketIds = selectedRepo.buckets
            ? selectedRepo.buckets
                .filter((bucket: any) => bucket.bucketId)
                .map((bucket: any) => bucket.bucketId)
            : [];

          // Find buckets in filteredBuckets that match these bucketIds
          const relatedBuckets = filteredBuckets.filter((bucket: any) => {
            return repoBucketIds.includes(bucket.id);
          });

          if (relatedBuckets.length > 0) {
            setSelectedBuckets((prevBuckets) => {
              const nextBuckets = new Set(prevBuckets);
              relatedBuckets.forEach((bucket: any) => {
                nextBuckets.delete(bucket.name);
              });
              return nextBuckets;
            });
          }
        }
      } else {
        next.add(name);

        // Auto-select related buckets when selecting a repository
        if (selectedRepo) {
          // Use the same approach as StorageRepoDetails: repo.buckets contains bucketId references
          const repoBucketIds = selectedRepo.buckets
            ? selectedRepo.buckets
                .filter((bucket: any) => bucket.bucketId || bucket.unitId || bucket.id)
                .map((bucket: any) => bucket.bucketId || bucket.unitId || bucket.id)
            : [];

          // Find buckets in filteredBuckets that match these bucketIds
          const relatedBuckets = filteredBuckets.filter((bucket: any) => {
            return repoBucketIds.includes(bucket.id);
          });

          if (relatedBuckets.length > 0) {
            setSelectedBuckets((prevBuckets) => {
              const nextBuckets = new Set(prevBuckets);
              relatedBuckets.forEach((bucket: any) => {
                nextBuckets.add(bucket.name);
              });
              return nextBuckets;
            });
          }
        }
      }

      return next;
    });
  };

  const toggleBucket = (name: string) => {
    setSelectedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  // Function to map storage type to provider name
  const getProviderNameFromStorageType = (storageType: any): string => {
    // Map storage types to provider names based on the API types
    switch (storageType) {
      case 'S3':
      case 'AWS_S3':
        return 'AWS';
      case 'GOOGLE_CLOUD':
      case 'GCS':
        return 'Google Cloud';
      case 'AZURE':
      case 'AZURE_BLOB':
        return 'Azure';
      case 'STORJ':
        return 'Storj';
      case 'FILECOIN':
        return 'FileCoin';
      case 'BACKBLAZE_B2':
        return 'Backblaze B2';
      case 'WASABI':
        return 'Wasabi';
      case 'DIGITALOCEAN_SPACES':
        return 'DigitalOcean Spaces';
      case 'OVH':
        return 'OVH';
      case 'S3_COMPATIBLE':
        return 'S3 Compatible';
      default:
        return 'Other';
    }
  };

  // Get unique providers from current workspace's repos and buckets
  const getWorkspaceProviders = (): string[] => {
    const providers = new Set<string>();

    // Add providers from filtered repos
    filteredRepos.forEach((repo: any) => {
      const providerName = getProviderNameFromStorageType(repo.storageType);
      providers.add(providerName);
    });

    // Add providers from filtered buckets
    filteredBuckets.forEach((bucket: any) => {
      const providerName = getProviderNameFromStorageType(bucket.storageType);
      providers.add(providerName);
    });

    return Array.from(providers).sort();
  };

  // Get the provider list to show (always show all providers, but filter repos/buckets by workspace)
  const getAvailableProviders = (): string[] => {
    // Always show all providers, regardless of workspace selection
    // The workspace filtering happens at the repo/bucket level, not provider level
    return providerList;
  };

  // Function to get filtered repo and bucket IDs for API calls (explicit selections only)
  const getFilteredIds = () => {
    const repoIds: string[] = [];
    const bucketIds: string[] = [];

    // Respect only manual selections; workspace scoping is applied to source lists, not API params
    // Explicit repo selections
    if (selectedRepositories.size > 0) {
      filteredRepos.forEach((repo: any) => {
        if (selectedRepositories.has(repo.name)) {
          repoIds.push(repo.id);
        }
      });
    }
    // Explicit bucket selections
    if (selectedBuckets.size > 0) {
      filteredBuckets.forEach((bucket: any) => {
        if (selectedBuckets.has(bucket.name)) {
          bucketIds.push(bucket.id);
        }
      });
    }

    return { repoIds, bucketIds };
  };

  // State to trigger data refresh when filters change
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Function to refresh data when filters change
  const refreshDataWithFilters = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // Function to clear all filters
  const clearAllFilters = () => {
    setSelectedProviders(new Set());
    setSelectedRepositories(new Set());
    setSelectedBuckets(new Set());
    // Immediately refresh and close drawer so results reflect cleared filters
    setTimeout(() => {
      refreshDataWithFilters();
      setFilterOpen(false);
    }, 100);
  };

  // Function to get active filters count
  const getActiveFiltersCount = () => {
    return selectedProviders.size + selectedBuckets.size;
  };

  // Auto-refresh data when any filter or workspace changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
    }, 150);
    return () => clearTimeout(timer);
  }, [selectedProviders, selectedRepositories, selectedBuckets, selectedWorkspaceId]);

  const presetToV2Window = (): DatePreset => {
    switch (selectedPreset) {
      case 'Today':
      case 'Yesterday':
        return '24h';
      case 'This week':
      case 'Last week':
        return '7d';
      case 'Last 30 days':
      case 'This month':
      case 'Last month':
        return '30d';
      case 'This year':
      case 'Last year':
        return '365d';
      case 'All time':
        return '365d';
      case 'Custom range': {
        if (selectedStartDate && selectedEndDate) {
          const diffDays = Math.ceil(
            (selectedEndDate.getTime() - selectedStartDate.getTime()) / (1000 * 60 * 60 * 24),
          );
          if (diffDays <= 1) return '24h';
          if (diffDays <= 7) return '7d';
          if (diffDays <= 30) return '30d';
          if (diffDays <= 90) return '90d';
          if (diffDays <= 180) return '180d';
          return '365d';
        }
        return '30d';
      }
      default:
        return '30d';
    }
  };

  // Fetch data via V2 Storage Gateway API
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      await new Promise((resolve) => setTimeout(resolve, 500));

      if (!isMounted) return;

      if (!listsLoaded) return;

      if (
        selectedWorkspaceId &&
        selectedWorkspaceId !== 'ALL' &&
        filteredRepos.length === 0 &&
        filteredBuckets.length === 0
      ) {
        setMetricsSummary({ averageUploaded: 0, averageDownloaded: 0, averageSizeChange: 0, totalRequests: 0 });
        setUploadChartData({ series: [], xAxisData: [] });
        setDownloadChartData({ series: [], xAxisData: [] });
        setStorageChartData({ series: [], xAxisData: [] });
        setRequestsChartData({ series: [], xAxisData: [] });
        setInitialLoading(false);
        setLoading(false);
        return;
      }

      if (filteredRepos.length === 0 && filteredBuckets.length === 0) {
        setInitialLoading(false);
        setLoading(false);
        return;
      }

      try {
        const dateRange = presetToV2Window();
        const wsId = selectedWorkspaceId && selectedWorkspaceId !== 'ALL' ? selectedWorkspaceId : null;
        const params = buildTimeSeriesParams(wsId, null, dateRange);

        const response: any = await withTokenRefresh(() =>
          apiClient.getStorageGatewayStats(params as any),
        );

        if (!isMounted) return;

        if (response.success && response.series && response.series.length > 0) {
          const series: Array<{ ts: number; uplBytes: number; dwlBytes: number; sizeChange: number; latencyMs: number; requests: number }> = response.series;
          const formatted = formatV2Series(series, dateRange);

          let sumUpl = 0;
          let sumDwl = 0;
          let sumChange = 0;
          let sumReq = 0;
          series.forEach((point) => {
            sumUpl += point.uplBytes;
            sumDwl += point.dwlBytes;
            sumChange += point.sizeChange;
            sumReq += point.requests;
          });

          // Override summary with per-bucket breakdown when specific buckets are selected
          try {
            const { bucketIds } = getFilteredIds();
            if (bucketIds.length > 0) {
              const bdParams = buildBreakdownParams(wsId, null, dateRange, { limit: 200 });
              const bdResponse: any = await withTokenRefresh(() =>
                apiClient.getStorageGatewayBreakdown('buckets', bdParams as any),
              );
              if (bdResponse?.success && bdResponse.entities?.length > 0) {
                const matched = bdResponse.entities.filter((e: any) => bucketIds.includes(e.entityId));
                if (matched.length > 0) {
                  let aggUp = 0;
                  let aggDown = 0;
                  let aggChange = 0;
                  let aggReq = 0;
                  matched.forEach((e: any) => {
                    aggUp += e.totals?.uplBytes || 0;
                    aggDown += e.totals?.dwlBytes || 0;
                    aggChange += e.totals?.sizeChange || 0;
                    aggReq += e.totals?.requests || 0;
                  });
                  if (aggUp > 0 || aggDown > 0 || aggChange > 0) {
                    sumUpl = aggUp;
                    sumDwl = aggDown;
                    sumChange = aggChange;
                    sumReq = aggReq;
                  }
                }
              }
            }
          } catch (_) { /* breakdown is optional */ }

          setMetricsSummary({
            averageUploaded: sumUpl / (1024 * 1024),
            averageDownloaded: sumDwl / (1024 * 1024),
            averageSizeChange: sumChange / (1024 * 1024),
            totalRequests: sumReq,
          });

          const xAxisData = formatted.map((d) => d.name);

          setUploadChartData({
            xAxisData,
            series: [{
              name: 'Upload Traffic',
              data: formatted.map((d) => d.uplBytes / (1024 * 1024 * 1024)),
              type: 'line',
              color: '#10B981',
            }],
          });

          setDownloadChartData({
            xAxisData,
            series: [{
              name: 'Download Traffic',
              data: formatted.map((d) => d.dwlBytes / (1024 * 1024 * 1024)),
              type: 'line',
              color: '#3B82F6',
            }],
          });

          setStorageChartData({
            xAxisData,
            series: [{
              name: 'Storage Change',
              data: formatted.map((d) => d.sizeChange / (1024 * 1024)),
              type: 'line',
              color: '#4CAF50',
            }],
          });

          setRequestsChartData({
            xAxisData,
            series: [{
              name: 'Total Requests',
              data: formatted.map((d) => d.requests),
              type: 'line',
              color: '#FF9800',
            }],
          });

          setInitialLoading(false);
        } else {
          setMetricsSummary({ averageUploaded: 0, averageDownloaded: 0, averageSizeChange: 0, totalRequests: 0 });
          setUploadChartData({ series: [], xAxisData: [] });
          setDownloadChartData({ series: [], xAxisData: [] });
          setStorageChartData({ series: [], xAxisData: [] });
          setRequestsChartData({ series: [], xAxisData: [] });
          setInitialLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError('Failed to fetch data: ' + (err instanceof Error ? err.message : String(err)));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          setTimeout(() => {
            if (isMounted) setInitialLoading(false);
          }, 300);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [
    selectedPreset,
    selectedStartDate,
    selectedEndDate,
    selectedWorkspaceId,
    selectedBuckets,
    selectedRepositories,
    selectedProviders,
    listsLoaded,
    filteredRepos,
    filteredBuckets,
  ]);

  // Formater les nombres pour l'affichage
  const formatNumber = (value: number, decimals = 2): string => {
    return value.toFixed(decimals);
  };

  // Fonction pour générer les options de graphique
  const getChartOptions = (chartData: ChartData, title: string, yAxisName: string) => {
    return {
      title: {
        text: title,
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'normal',
          color: '#333',
        },
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: '#ddd',
        textStyle: { color: '#333' },
      },
      legend: {
        data: chartData.series.map((s) => s.name),
        bottom: 0,
        textStyle: { color: '#333' },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: chartData.xAxisData,
        axisLabel: {
          color: '#333',
          rotate: 45,
          fontSize: 10,
        },
        axisLine: { lineStyle: { color: '#999' } },
      },
      yAxis: {
        type: 'value',
        name: yAxisName,
        axisLabel: { color: '#333' },
        axisLine: { lineStyle: { color: '#999' } },
        splitLine: { lineStyle: { color: 'rgba(0, 0, 0, 0.1)' } },
      },
      series: chartData.series.map((series) => ({
        ...series,
        smooth: true,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: {
          width: 2,
        },
        emphasis: {
          focus: 'series',
          lineStyle: {
            width: 4,
          },
        },
      })),
    };
  };

  // Afficher un indicateur de chargement amélioré
  if (initialLoading) {
    return (
      <Box
        className="flex flex-col justify-center items-center"
        sx={{ height: 'calc(100vh - 64px)', gap: 2 }}
      >
        <CircularProgress size={40} />
        <Typography sx={{ color: '#667085', fontSize: 14 }}>
          {!listsLoaded ? 'Loading workspace data...' : 'Fetching metrics...'}
        </Typography>
      </Box>
    );
  }

  // Afficher un message d'erreur
  if (error) {
    return (
      <Box className="flex justify-center items-center" sx={{ height: 'calc(100vh - 64px)' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ padding: '24px 40px 24px 40px' }}>
        <Typography
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          Cloud Storage
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
          }}
        >
          Manage your cloud storage buckets and repositories
        </Typography>
      </div>

      {/* Content */}
      <div className="flex-1 px-10 pb-6 overflow-y-auto">
      <Grid container spacing={3}>
        {/* Statistics Sub-section Header */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              mb: 3,
              ml: '8px',
            }}
          >
            {/* Left: Title + Subtitle */}
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
                Statistics
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#667085' }}>
                {selectedWorkspaceId && selectedWorkspaceId !== 'ALL'
                  ? `Showing data for workspace: ${
                      globalData.workspaces.find((w: any) => w.id === selectedWorkspaceId)?.name ||
                      selectedWorkspaceId
                    }`
                  : 'View detailed metrics and performance analytics'}
              </Typography>
            </Box>

            {/* Right: Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Reload */}
              <Tooltip title="Reload">
                <IconButton
                  size="small"
                  onClick={() => setRefreshTrigger((prev) => prev + 1)}
                  disabled={loading}
                  sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              {/* Filter */}
              <Tooltip title={`Filter${getActiveFiltersCount() > 0 ? ` (${getActiveFiltersCount()})` : ''}`}>
                <IconButton
                  size="small"
                  onClick={() => setFilterOpen(true)}
                  sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
                >
                  <FilterListIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              {/* Date range */}
              <Tooltip title="Date range">
                <IconButton
                  size="small"
                  onClick={openDatePicker}
                  sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
                >
                  <CalendarTodayIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>

              {/* Date Range Popover */}
              <Popover
                open={datePickerOpen}
                anchorEl={dateAnchorEl}
                onClose={closeDatePicker}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    width: 808,
                    height: 460,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow:
                      '0px 12px 16px rgba(16, 24, 40, 0.08), 0px 4px 6px rgba(16, 24, 40, 0.03)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', height: '100%', bgcolor: '#FFFFFF' }}>
                  {/* Left presets column */}
                  <Box
                    sx={{
                      width: 152,
                      borderRight: '1px solid #EAECF0',
                      py: 2,
                      px: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}
                  >
                    {[
                      'Last 30 days',
                      'Today',
                      'Yesterday',
                      'This week',
                      'Last week',
                      'This month',
                      'Last month',
                      'This year',
                      'Last year',
                      'All time',
                    ].map((label) => (
                      <Button
                        key={label}
                        variant="text"
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          color: '#414651',
                          fontWeight: 500,
                          fontSize: 14,
                          lineHeight: '20px',
                          height: 36,
                          px: '12px',
                          py: '8px',
                          borderRadius: '6px',
                          minWidth: 'auto',
                          width: '100%',
                          backgroundColor: selectedPreset === label ? '#FAFAFA' : 'transparent',
                          '&:hover': { backgroundColor: '#FAFAFA' },
                        }}
                        onClick={() => setSelectedPreset(label)}
                      >
                        {label}
                      </Button>
                    ))}
                  </Box>

                  {/* Right main calendar area */}
                  <Box sx={{ width: 656, position: 'relative', bgcolor: '#FFFFFF' }}>
                    <Box sx={{ display: 'flex', gap: 0, p: 0 }}>
                      {/* Simple month renderer */}
                      {[0, 1].map((offset) => (
                        <Box key={offset} sx={{ flex: 1, minWidth: 0 }}>
                          <MonthView
                            offset={offset}
                            startDate={selectedStartDate}
                            endDate={selectedEndDate}
                            baseDate={calendarBaseDate}
                            onPrevMonth={
                              offset === 0
                                ? () =>
                                    setCalendarBaseDate(
                                      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
                                    )
                                : undefined
                            }
                            onNextMonth={
                              offset === 1
                                ? () =>
                                    setCalendarBaseDate(
                                      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
                                    )
                                : undefined
                            }
                            onSelect={(d) => {
                              // Manual selection -> custom range
                              if (!selectedStartDate) {
                                // Premier clic : définir start et end à la même date (jour unique)
                                setSelectedStartDate(d);
                                setSelectedEndDate(d);
                                setSelectedPreset('Custom range');
                              } else if (selectedStartDate && selectedEndDate) {
                                // On a déjà une sélection
                                if (
                                  selectedStartDate.toDateString() ===
                                  selectedEndDate.toDateString()
                                ) {
                                  // Actuellement un jour unique sélectionné
                                  if (d.toDateString() === selectedStartDate.toDateString()) {
                                    // Double-clic sur le même jour : confirmer la sélection
                                    setSelectedStartDate(d);
                                    setSelectedEndDate(d);
                                    setSelectedPreset('Custom range');
                                  } else {
                                    // Clic sur autre jour : créer une plage
                                    if (d.getTime() < selectedStartDate.getTime()) {
                                      setSelectedEndDate(selectedStartDate);
                                      setSelectedStartDate(d);
                                    } else {
                                      setSelectedEndDate(d);
                                    }
                                    setSelectedPreset('Custom range');
                                  }
                                } else {
                                  // Actuellement une plage sélectionnée, tout clic = nouveau jour unique
                                  setSelectedStartDate(d);
                                  setSelectedEndDate(d);
                                  setSelectedPreset('Custom range');
                                }
                              }
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                    {/* Footer */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 72,
                        borderTop: '1px solid #EAECF0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 16 / 8,
                        py: 1.5,
                        bgcolor: '#FFFFFF',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box
                          sx={{
                            border: '1px solid #EAECF0',
                            borderRadius: '8px',
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            px: 1.5,
                            color: '#667085',
                            minWidth: 180,
                          }}
                        >
                          <Typography variant="body2">
                            {selectedStartDate ? formatDate(selectedStartDate) : 'Start date'}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            border: '1px solid #EAECF0',
                            borderRadius: '8px',
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            px: 1.5,
                            color: '#667085',
                            minWidth: 180,
                          }}
                        >
                          <Typography variant="body2">
                            {selectedEndDate ? formatDate(selectedEndDate) : 'End date'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          onClick={closeDatePicker}
                          sx={{ textTransform: 'none', color: '#344054' }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            backgroundColor: '#10B981',
                            '&:hover': { backgroundColor: '#059669' },
                            boxShadow: 'none',
                          }}
                          onClick={closeDatePicker}
                        >
                          Apply
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Popover>

              {/* Search bar 320x40 */}
              <Box
                sx={{
                  width: 320,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  pt: '8px',
                  pr: '12px',
                  pb: '8px',
                  pl: '12px',
                  borderRadius: '8px',
                  border: '1px solid #EAECF0',
                  backgroundColor: '#FFFFFF',
                }}
              >
                <img src={searchIcon} alt="Search" width={16} height={16} />
                <input
                  type="text"
                  placeholder="Search"
                  style={{
                    border: 'none',
                    outline: 'none',
                    width: '100%',
                    fontSize: 14,
                    lineHeight: '20px',
                    color: '#101828',
                    background: 'transparent',
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Right sidebar filter drawer */}
        <Drawer
          anchor="right"
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          keepMounted
          PaperProps={{
            sx: {
              width: 400,
              backgroundColor: '#FFFFFF',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            },
          }}
        >
          {/* Header */}
          <Box sx={{ p: 2 }}>
            <Box sx={{ width: 352, mx: 'auto' }}>
              <Typography variant="h6" sx={{ color: '#101828', fontWeight: 600 }}>
                Filters
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085' }}>
                Apply filters to your report.
              </Typography>
            </Box>
          </Box>
          <Divider />

          {/* Content */}
          <Box sx={{ p: 2, overflowY: 'auto' }}>
            <Box sx={{ width: 352, mx: 'auto' }}>
              {/* Saved filter select (visual only) */}
              <Box
                sx={{
                  width: 352,
                  height: 40,
                  pt: '8px',
                  pr: '12px',
                  pb: '8px',
                  pl: '12px',
                  gap: '8px',
                  border: '1px solid #EAECF0',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: '#667085',
                  mx: 'auto',
                }}
              >
                <span>Select saved filter</span>
                <span style={{ fontSize: 18, lineHeight: '18px' }}>▾</span>
              </Box>

              {/* Providers */}
              <Box sx={{ mt: 3 }}>
                <Typography sx={{ color: '#344054', fontWeight: 600, mb: 1 }}>Providers</Typography>
                <Box>
                  {(() => {
                    const providersToRender =
                      selectedWorkspaceId && selectedWorkspaceId !== 'ALL'
                        ? getWorkspaceProviders()
                        : getAvailableProviders();
                    return (
                      <>
                        {/* All providers option (hide if none) */}
                        {providersToRender.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Checkbox
                              size="small"
                              checked={selectedProviders.size === 0}
                              onChange={() => setSelectedProviders(new Set())}
                            />
                            <Typography
                              sx={{
                                color: '#414651',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '20px',
                              }}
                            >
                              All providers
                            </Typography>
                          </Box>
                        )}
                        {(providerExpanded ? providersToRender : providersToRender.slice(0, 5)).map(
                          (name) => (
                            <Box
                              key={name}
                              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                            >
                              <Checkbox
                                size="small"
                                checked={selectedProviders.has(name)}
                                onChange={() => toggleProvider(name)}
                              />
                              {/* Provider logo (fallback to placeholder) */}
                              {providerIcons[name] ? (
                                <img src={providerIcons[name]} alt={name} width={20} height={20} />
                              ) : (
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    background: '#F2F4F7',
                                  }}
                                />
                              )}
                              <Typography
                                sx={{
                                  color: '#414651',
                                  fontWeight: 500,
                                  fontSize: '14px',
                                  lineHeight: '20px',
                                }}
                              >
                                {name}
                              </Typography>
                            </Box>
                          )
                        )}
                        {!providerExpanded && providersToRender.length > 5 && (
                          <Box
                            component="button"
                            onClick={() => setProviderExpanded(true)}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              color: '#344054',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            Show {providersToRender.length - 5} more
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </Box>
              </Box>

              {/* Buckets */}
              <Box sx={{ mt: 3, mb: 10 }}>
                <Typography sx={{ color: '#344054', fontWeight: 600, mb: 1 }}>Buckets</Typography>
                {/* Search */}
                <Box
                  sx={{
                    width: 352,
                    height: 40,
                    pt: '8px',
                    pr: '12px',
                    pb: '8px',
                    pl: '12px',
                    gap: '8px',
                    border: '1px solid #EAECF0',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#667085',
                    mb: 1.5,
                    mx: 'auto',
                  }}
                >
                  <img src={searchIcon} alt="Search" width={16} height={16} />
                  <span>Search</span>
                </Box>
                {/* All buckets option (hide if none) */}
                {bucketOptions.length > 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedBuckets.size === 0}
                        onChange={() => setSelectedBuckets(new Set())}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          color: '#414651',
                          fontWeight: 500,
                          fontSize: '14px',
                          lineHeight: '20px',
                        }}
                      >
                        All buckets
                      </Typography>
                    }
                    sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                  />
                )}
                {(bucketExpanded ? bucketOptions : bucketOptions.slice(0, 5)).map((name) => (
                  <FormControlLabel
                    key={name}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedBuckets.has(name)}
                        onChange={() => toggleBucket(name)}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          color: '#414651',
                          fontWeight: 500,
                          fontSize: '14px',
                          lineHeight: '20px',
                        }}
                      >
                        {name}
                      </Typography>
                    }
                    sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                  />
                ))}
                {!bucketExpanded && bucketOptions.length > 5 && (
                  <Box
                    component="button"
                    onClick={() => setBucketExpanded(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: '#344054',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Show {bucketOptions.length - 5} more
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Footer actions */}
          <Divider />
          <Box
            sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Box
              component="button"
              onClick={clearAllFilters}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: '#344054',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Clear all ({getActiveFiltersCount()})
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                component="button"
                onClick={() => setFilterOpen(false)}
                style={{
                  height: 36,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: '1px solid #EAECF0',
                  background: '#FFFFFF',
                  color: '#101828',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </Box>
              <Box
                component="button"
                onClick={() => {
                  refreshDataWithFilters();
                  setFilterOpen(false);
                }}
                style={{
                  height: 36,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: '1px solid #667085',
                  background: '#10B981',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Apply Filters
              </Box>
            </Box>
          </Box>
        </Drawer>

        {/* Cartes de métriques */}
        <Grid item xs={12}>
          <Grid
            container
            columnSpacing={2}
            rowSpacing={2}
            sx={{ width: '100%', mx: 'auto', px: 2 }}
          >
            {/* Carte Total Uploaded */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  border: '1px solid #E9EAEB',
                  boxShadow: '0 1px 2px 0 rgba(16,24,40,0.05)',
                  borderRadius: 2,
                  bgcolor: '#FFFFFF',
                }}
              >
                <CardContent>
                  <div className="flex justify-between items-center mb-2">
                    <Typography variant="subtitle2" color="textSecondary">
                      Total Upload
                    </Typography>
                    <TrendingUpIcon style={{ color: '#10B981' }} />
                  </div>
                  <Typography variant="h5" component="div" fontWeight="bold">
                    {formatNumber(metricsSummary.averageUploaded)} MB
                  </Typography>
                  <Typography variant="body2" color="textSecondary" className="mt-1">
                    All time
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Carte Total Downloaded */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  border: '1px solid #E9EAEB',
                  boxShadow: '0 1px 2px 0 rgba(16,24,40,0.05)',
                  borderRadius: 2,
                  bgcolor: '#FFFFFF',
                }}
              >
                <CardContent>
                  <div className="flex justify-between items-center mb-2">
                    <Typography variant="subtitle2" color="textSecondary">
                      Total Download
                    </Typography>
                    <TrendingDownIcon style={{ color: '#3B82F6' }} />
                  </div>
                  <Typography variant="h5" component="div" fontWeight="bold">
                    {formatNumber(metricsSummary.averageDownloaded)} MB
                  </Typography>
                  <Typography variant="body2" color="textSecondary" className="mt-1">
                    All time
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Carte Storage Change */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  border: '1px solid #E9EAEB',
                  boxShadow: '0 1px 2px 0 rgba(16,24,40,0.05)',
                  borderRadius: 2,
                  bgcolor: '#FFFFFF',
                }}
              >
                <CardContent>
                  <div className="flex justify-between items-center mb-2">
                    <Typography variant="subtitle2" color="textSecondary">
                      Average Storage Change
                    </Typography>
                    <StorageIcon style={{ color: '#8e9dff' }} />
                  </div>
                  <Typography variant="h5" component="div" fontWeight="bold">
                    {formatNumber(metricsSummary.averageSizeChange)} MB
                  </Typography>
                  <Typography variant="body2" color="textSecondary" className="mt-1">
                    All time
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Carte Total Requests */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  border: '1px solid #E9EAEB',
                  boxShadow: '0 1px 2px 0 rgba(16,24,40,0.05)',
                  borderRadius: 2,
                  bgcolor: '#FFFFFF',
                }}
              >
                <CardContent>
                  <div className="flex justify-between items-center mb-2">
                    <Typography variant="subtitle2" color="textSecondary">
                      Total Requests
                    </Typography>
                    <SpeedIcon style={{ color: '#f0c7b1' }} />
                  </div>
                  <Typography variant="h5" component="div" fontWeight="bold">
                    {formatNumber(metricsSummary.totalRequests)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" className="mt-1">
                    All time
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Close metrics cards grid and item */}
          </Grid>
        </Grid>

        {/* Graphiques */}
        <Grid item xs={12}>
          <Grid
            container
            columnSpacing={2}
            rowSpacing={4}
            justifyContent="center"
            sx={{ width: '100%', mx: 'auto', px: 2 }}
          >
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: '100%',
                  height: 444,
                  bgcolor: '#F5F5F5',
                  borderRadius: 2,
                  p: 0,
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  overflow: 'visible',
                }}
              >
                <Typography
                  sx={{
                    color: '#181D27',
                    fontWeight: 600,
                    fontSize: 14,
                    lineHeight: '20px',
                    textAlign: 'left',
                    width: '100%',
                    mb: 1,
                    px: 2,
                  }}
                >
                  <>
                    Upload Traffic (MB){getSelectedRangeLabel() && ` - ${getSelectedRangeLabel()}`}
                  </>
                </Typography>
                <Card
                  sx={{
                    width: '100%',
                    height: 404,
                    borderRadius: 2,
                    border: '1px solid #E9EAEB',
                    boxShadow: '0 1px 2px 0 rgba(16,24,40,0.05)',
                    bgcolor: '#FFFFFF',
                    mx: 'auto',
                  }}
                >
                  <CardContent sx={{ p: 2, height: '100%' }}>
                    <ResponsiveContainer width="100%" height={372}>
                      <AreaChart
                        data={uploadChartData.xAxisData.map((date, index) => ({
                          name: date,
                          value: uploadChartData.series[0]?.data[index] || 0,
                        }))}
                        margin={{ top: 10, right: 30, left: 10, bottom: 5 }}
                      >
                        <defs>
                          <linearGradient id="colorUploadMetrics" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 11, fill: '#6B7280' }}
                          stroke="#E5E7EB"
                          tickLine={false}
                        />
                        <YAxis
                          domain={[0, (dataMax: number) => roundToNiceDomain(dataMax)]}
                          ticks={(() => {
                            const values = uploadChartData.series[0]?.data || [];
                            const dataMax = Math.max(...values, 0);
                            const max = roundToNiceDomain(dataMax);
                            const ticks: number[] = [];
                            const step = max / 5;
                            for (let i = 0; i <= 5; i++) {
                              ticks.push(step * i);
                            }
                            return ticks;
                          })()}
                          tick={{ fontSize: 11, fill: '#6B7280' }}
                          stroke="#E5E7EB"
                          tickLine={false}
                          tickFormatter={(value: number) => {
                            if (value >= 1) return `${value.toFixed(1)} GB`;
                            if (value >= 0.01) return `${value.toFixed(2)} GB`;
                            return `${(value * 1024).toFixed(0)} MB`;
                          }}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                          }}
                          formatter={(value: any) => {
                            const gb = Number(value);
                            if (gb >= 1) return [`${gb.toFixed(2)} GB`, 'Upload'];
                            if (gb >= 0.01) return [`${gb.toFixed(3)} GB`, 'Upload'];
                            return [`${(gb * 1024).toFixed(1)} MB`, 'Upload'];
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke="#10B981"
                          fill="url(#colorUploadMetrics)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: '100%',
                  height: 444,
                  bgcolor: '#F5F5F5',
                  borderRadius: 2,
                  p: 0,
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  overflow: 'visible',
                }}
              >
                <Typography
                  sx={{
                    color: '#181D27',
                    fontWeight: 600,
                    fontSize: 14,
                    lineHeight: '20px',
                    textAlign: 'left',
                    width: '100%',
                    mb: 1,
                    px: 2,
                  }}
                >
                  <>
                    Download Traffic (GB)
                    {getSelectedRangeLabel() && ` - ${getSelectedRangeLabel()}`}
                  </>
                </Typography>
                <Card
                  sx={{
                    width: '100%',
                    height: 404,
                    borderRadius: 2,
                    border: '1px solid #E9EAEB',
                    boxShadow: '0 1px 2px 0 rgba(16,24,40,0.05)',
                    bgcolor: '#FFFFFF',
                    mx: 'auto',
                  }}
                >
                  <CardContent sx={{ p: 2, height: '100%' }}>
                    <ResponsiveContainer width="100%" height={372}>
                      <AreaChart
                        data={downloadChartData.xAxisData.map((date, index) => ({
                          name: date,
                          value: downloadChartData.series[0]?.data[index] || 0,
                        }))}
                        margin={{ top: 10, right: 30, left: 10, bottom: 5 }}
                      >
                        <defs>
                          <linearGradient id="colorDownloadMetrics" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 11, fill: '#6B7280' }}
                          stroke="#E5E7EB"
                          tickLine={false}
                        />
                        <YAxis
                          domain={[0, (dataMax: number) => roundToNiceDomain(dataMax)]}
                          ticks={(() => {
                            const values = downloadChartData.series[0]?.data || [];
                            const dataMax = Math.max(...values, 0);
                            const max = roundToNiceDomain(dataMax);
                            const ticks: number[] = [];
                            const step = max / 5;
                            for (let i = 0; i <= 5; i++) {
                              ticks.push(step * i);
                            }
                            return ticks;
                          })()}
                          tick={{ fontSize: 11, fill: '#6B7280' }}
                          stroke="#E5E7EB"
                          tickLine={false}
                          tickFormatter={(value: number) => {
                            if (value >= 1) return `${value.toFixed(1)} GB`;
                            if (value >= 0.01) return `${value.toFixed(2)} GB`;
                            return `${(value * 1024).toFixed(0)} MB`;
                          }}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                          }}
                          formatter={(value: any) => {
                            const gb = Number(value);
                            if (gb >= 1) return [`${gb.toFixed(2)} GB`, 'Download'];
                            if (gb >= 0.01) return [`${gb.toFixed(3)} GB`, 'Download'];
                            return [`${(gb * 1024).toFixed(1)} MB`, 'Download'];
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke="#3B82F6"
                          fill="url(#colorDownloadMetrics)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: '100%',
                  height: 444,
                  bgcolor: '#F5F5F5',
                  borderRadius: 2,
                  p: 0,
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  overflow: 'visible',
                }}
              >
                <Typography
                  sx={{
                    color: '#181D27',
                    fontWeight: 600,
                    fontSize: 14,
                    lineHeight: '20px',
                    textAlign: 'left',
                    width: '100%',
                    mb: 1,
                    px: 2,
                  }}
                >
                  <>
                    Storage Growth (MB){getSelectedRangeLabel() && ` - ${getSelectedRangeLabel()}`}
                  </>
                </Typography>
                <Card
                  sx={{
                    width: '100%',
                    height: 404,
                    borderRadius: 2,
                    border: '1px solid #E9EAEB',
                    boxShadow: '0 1px 2px 0 rgba(16,24,40,0.05)',
                    bgcolor: '#FFFFFF',
                    mx: 'auto',
                  }}
                >
                  <CardContent sx={{ p: 2, height: '100%' }}>
                    <ReactECharts
                      option={{
                        tooltip: {
                          trigger: 'axis',
                          formatter: (params: any) => {
                            const param = params[0];
                            return `${param.name}: ${param.value.toFixed(2)} MB`;
                          },
                        },
                        legend: {
                          data: ['Average Size Change'],
                          bottom: 0,
                          textStyle: { color: '#333' },
                        },
                        grid: {
                          left: '3%',
                          right: '4%',
                          bottom: '15%',
                          containLabel: true,
                        },
                        xAxis: {
                          type: 'category',
                          data: storageChartData.xAxisData,
                          axisLabel: {
                            color: '#333',
                            interval: Math.max(
                              1,
                              Math.floor(storageChartData.xAxisData.length / 8)
                            ),
                          },
                          axisLine: { lineStyle: { color: '#999' } },
                        },
                        yAxis: {
                          type: 'value',
                          name: 'MB',
                          axisLabel: { color: '#333' },
                          axisLine: { lineStyle: { color: '#999' } },
                          splitLine: { lineStyle: { color: 'rgba(0, 0, 0, 0.1)' } },
                        },
                        series: storageChartData.series.map((series) => ({
                          ...series,
                          name: 'Average Size Change',
                        })),
                      }}
                      style={{ height: 372 }}
                    />
                  </CardContent>
                </Card>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: '100%',
                  height: 444,
                  bgcolor: '#F5F5F5',
                  borderRadius: 2,
                  p: 0,
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  overflow: 'visible',
                }}
              >
                <Typography
                  sx={{
                    color: '#181D27',
                    fontWeight: 600,
                    fontSize: 14,
                    lineHeight: '20px',
                    textAlign: 'left',
                    width: '100%',
                    mb: 1,
                    px: 2,
                  }}
                >
                  <>Total Requests{getSelectedRangeLabel() && ` - ${getSelectedRangeLabel()}`}</>
                </Typography>
                <Card
                  sx={{
                    width: '100%',
                    height: 404,
                    borderRadius: 2,
                    border: '1px solid #E9EAEB',
                    boxShadow: '0 1px 2px 0 rgba(16,24,40,0.05)',
                    bgcolor: '#FFFFFF',
                    mx: 'auto',
                  }}
                >
                  <CardContent sx={{ p: 2, height: '100%' }}>
                    <ReactECharts
                      option={getChartOptions(requestsChartData, '', '')}
                      style={{ height: 372 }}
                    />
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      </div>
    </div>
  );
};

export default MetricsDashboard;
